import React, { useState } from 'react';
import { ExternalLink, Info } from 'lucide-react';

// Modelo do boleto único dentro do aviso de aceite (#5575). O cliente lê a cláusula e
// vê, na mesma tela, o documento que vai receber — o texto sozinho não mostra que os
// dois valores passam a sair num total só.
//
// POR QUE IMAGEM E NÃO UM DESENHO EM HTML:
//   O boleto é o layout v6 de verdade, gerado por scripts/preview-boleto-vigor-v6.ts
//   com dados fictícios e `ucAderiuBoletoUnico: true` — é o mesmo desenho que a edge
//   function usa em produção. Reproduzir isso em HTML criaria um segundo layout para
//   manter em sincronia, e ele desandaria no primeiro ajuste do PDF.
//
// POR QUE OS DADOS SÃO OBVIAMENTE FALSOS:
//   "Maria da Silva", UC e CPF zerados, valores redondos e a tarja "EXEMPLO
//   ILUSTRATIVO" na diagonal. Ninguém pode confundir a imagem com a própria conta —
//   nem tentar pagar o código de barras, que também é zerado.
//
// A imagem fica no bucket público `avisos-anexos` (o mesmo dos anexos de aviso) para
// não entrar no bundle: são 79 KB que só baixam quando o cliente abre o aviso.

const URL_EXEMPLO =
  'https://ilsxivnbuwxhslmonzna.supabase.co/storage/v1/object/public/avisos-anexos/boleto-unico/exemplo-boleto-unico.webp';
const URL_EXEMPLO_GRANDE =
  'https://ilsxivnbuwxhslmonzna.supabase.co/storage/v1/object/public/avisos-anexos/boleto-unico/exemplo-boleto-unico-grande.webp';

// Alt curto de propósito: quem usa leitor de tela já ouviu a descrição no parágrafo
// visível logo acima (também lido pelo leitor, na ordem natural da página) — repetir o
// mesmo texto inteiro dentro do alt só duplica a leitura no meio da cláusula.
const ALT_IMAGEM = 'Exemplo do boleto único da Vigor, com a fatura da Equatorial incluída e um total geral no fim.';

const ExemploBoletoUnico = () => {
  // Se a imagem não carregar (rede ruim, bucket fora), o bloco não some: o cliente que já
  // leu "Como vai ficar o seu boleto" ficaria com a impressão de que a cláusula pulou uma
  // parte, ou que o aviso quebrou. Fica um aviso curto no lugar da imagem.
  const [falhou, setFalhou] = useState(false);

  return (
    <section className="mt-6" aria-labelledby="exemplo-boleto-titulo">
      <h3 id="exemplo-boleto-titulo" className="text-base font-bold text-gray-900">
        Como vai ficar o seu boleto
      </h3>
      <p className="text-sm text-gray-600 mt-1">
        Os dois valores aparecem no mesmo documento, cada um na sua linha, com um total só. No exemplo abaixo, a
        fatura da Equatorial entra como uma linha de R$ 120,00 e o total do boleto — Vigor + Equatorial — soma
        R$ 363,00.
      </p>

      {falhou ? (
        <p className="mt-3 flex items-start gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
          <Info size={13} className="shrink-0 mt-0.5" aria-hidden="true" />
          Não foi possível carregar a imagem de exemplo agora. Isso não muda as condições descritas acima.
        </p>
      ) : (
        <figure className="mt-3">
          {/* Altura travada em telas de celular: a foto do boleto inteiro (900x1273) sem limite
              empurraria a lista de unidades e o botão de aceite pra muito longe da tela — o
              cliente rolando pelo celular pode achar que já viu tudo e desistir antes de chegar
              lá. object-contain preserva a proporção; quem quiser ver cheio usa o link abaixo. */}
          <img
            src={URL_EXEMPLO}
            alt={ALT_IMAGEM}
            loading="lazy"
            width={900}
            height={1273}
            onError={() => setFalhou(true)}
            // w-auto + mx-auto em vez de w-full: com `object-contain` numa caixa de largura
            // cheia, a moldura ficava com duas faixas cinzas dos lados da folha. Deixando a
            // largura seguir a altura limitada, a borda encosta no boleto.
            className="max-h-[45vh] sm:max-h-80 w-auto max-w-full mx-auto block rounded-xl border border-gray-200 bg-white"
          />
          <a
            href={URL_EXEMPLO_GRANDE}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80"
          >
            Abrir o exemplo em tamanho maior
            <ExternalLink size={12} aria-hidden="true" />
          </a>
          <figcaption className="mt-2 flex items-start gap-1.5 text-xs text-gray-400">
            <Info size={13} className="shrink-0 mt-0.5" aria-hidden="true" />
            Exemplo ilustrativo: nome, unidade e valores são fictícios e não são da sua conta.
          </figcaption>
        </figure>
      )}
    </section>
  );
};

export default ExemploBoletoUnico;
